import { CubePresenter, Logger } from 'wdc-cube'

import { type ExportDataStatus } from 'src/modules/the-consumer-market/tcm_service'
import { MainPresenter } from '.'
import { alreadyExportedRowKey, ConfirmExportDataLimitScope } from './Main.scopes'

const LOG = Logger.get('ConfirmExportDataLimitPresenter')

export default class ConfirmExportDataLimitPresenter extends CubePresenter<MainPresenter, ConfirmExportDataLimitScope> {
    // :: Class methods

    constructor(owner: MainPresenter, scope: ConfirmExportDataLimitScope) {
        super(owner, scope)
        this.__parent = owner
        LOG.debug('** init')
    }

    // :: Instance

    private readonly __parent: MainPresenter

    // :: Public API

    async initialize() {
        this.scope.update = this.update
        this.scope.onQttExportRecordsChange = this.action(this.__onQttExportRecordsChange)
        this.scope.onCheckLimitExportData = this.action(this.__onCheckLimitExportData)
        this.scope.onToggleAlreadyExported = this.action(this.__onToggleAlreadyExported)
        this.scope.onToggleAllAlreadyExported = this.action(this.__onToggleAllAlreadyExported)
    }

    override release(): void {
        super.release()
    }

    async __onQttExportRecordsChange(qtt: number) {
        this.scope.qttExportRecords = qtt > 0 ? qtt : undefined
        this.__recalc()
    }

    async __onToggleAlreadyExported(key: string) {
        const row = this.scope.alreadyExported.find((it) => alreadyExportedRowKey(it) === key)
        if (!row) {
            return
        }

        this.scope.alreadyExported = this.scope.alreadyExported.map((it) =>
            it === row ? { ...it, included: !it.included } : it
        )

        this.__syncAllIncludedFlag()
        this.__recalc()
    }

    async __onToggleAllAlreadyExported() {
        const included = !this.scope.allAlreadyExportedIncluded

        this.scope.alreadyExported = this.scope.alreadyExported.map((it) => ({ ...it, included }))
        this.scope.allAlreadyExportedIncluded = included
        this.__recalc()
    }

    async __onCheckLimitExportData(checkExportData: ExportDataStatus) {
        if (!checkExportData) {
            return
        }

        this.scope.canExport = checkExportData.canExport
        this.scope.unlimitedDataExport = checkExportData.unlimitedDataExport
        this.scope.blockExportWhenExceeded = !!checkExportData.blockExportWhenExceeded
        this.scope.message = checkExportData.message
        this.scope.limitExportData = checkExportData.limitExportData || 0
        this.scope.currentMonthExportedData = checkExportData.currentMonthExportedData || 0
        this.scope.requestExportQtdd = checkExportData.requestExportQtdd || 0
        this.scope.reportType = checkExportData.reportType

        this.scope.alreadyExported = (checkExportData.alreadyExported || []).map((doc) => ({
            ...doc,
            included: false
        }))
        this.scope.allAlreadyExportedIncluded = false
        this.scope.qttExportRecords = undefined

        this.__recalc()

        this.scope.open = true
    }

    private __syncAllIncludedFlag() {
        const rows = this.scope.alreadyExported
        this.scope.allAlreadyExportedIncluded = rows.length > 0 && rows.every((it) => it.included)
    }

    private __recalc() {
        const excluded = this.scope.alreadyExported.reduce((acc, it) => (it.included ? acc : acc + 1), 0)
        const available = Math.max((this.scope.requestExportQtdd || 0) - excluded, 0)
        this.scope.availableExportQtdd = available

        if (this.scope.qttExportRecords !== undefined) {
            this.scope.qttExportRecords = Math.max(Math.min(this.scope.qttExportRecords, available), 0) || undefined
        }

        const qttDownloadRecords = this.scope.qttExportRecords || available
        const current = this.scope.currentMonthExportedData || 0
        const limit = this.scope.limitExportData || 0

        this.scope.newTotal = current + qttDownloadRecords
        this.scope.percent = limit > 0 ? (this.scope.newTotal / limit) * 100 : 0
        this.scope.exceeded = !this.scope.unlimitedDataExport && this.scope.percent > 100
        this.scope.blocked = this.scope.exceeded && this.scope.blockExportWhenExceeded
    }
}
